import React, { useId } from 'react';
import { Bell, Mail, MessageSquare, Send, Smartphone } from 'lucide-react';
import { useI18n } from '../../../i18n/I18nContext';
import { CHANNELS, channelAvailable } from '../reminderUi';
import './Composer.css';

export const CHANNEL_ICONS = {
  IN_APP: Bell,
  PUSH: Smartphone,
  TELEGRAM: Send,
  SMS: MessageSquare,
  EMAIL: Mail,
};

/**
 * Which channels a message goes through: one checkbox per channel and the "SMS faqat boshqa kanal
 * yetmasa" switch. A channel the server has no credentials for (GET /crm/reminders/channels says
 * so) is disabled and says "Sozlanmagan"; the in-app notification is always on.
 *
 *   <ChannelPicker value={['IN_APP', 'PUSH']} onChange={setChannels} channels={options.channels}
 *                  smsFallbackOnly={fallback} onSmsFallbackChange={setFallback} />
 *
 * `channels` is the API's answer (or null when it could not be read: nothing is blocked then).
 * `value` is the list of channel keys; the picker hands back the list in the standard order.
 */
export default function ChannelPicker({
  value,
  onChange,
  channels,
  smsFallbackOnly,
  onSmsFallbackChange,
}) {
  const { t } = useI18n();
  const uid = useId();
  const picked = new Set(value);
  const smsOn = picked.has('SMS') && channelAvailable(channels, 'SMS');

  const toggle = (key, on) => {
    const next = new Set(picked);
    if (on) next.add(key);
    else next.delete(key);
    // The standard order, IN_APP always in; an unavailable channel the rule already had stays as it was.
    const list = CHANNELS.filter((k) => k === 'IN_APP' || next.has(k));
    onChange(list);
  };

  return (
    <fieldset className="rmc-channels">
      <legend className="ui-label">{t('reminders.channels.title')}</legend>
      <div className="rmc-channel-list">
        {CHANNELS.map((key) => {
          const Icon = CHANNEL_ICONS[key];
          const available = channelAvailable(channels, key);
          const locked = key === 'IN_APP';
          const hint = !available
            ? t('reminders.channels.notConfigured')
            : locked
              ? t('reminders.channels.alwaysOn')
              : t(`reminders.channels.hint.${key}`);
          return (
            <label key={key} className={`rmc-channel${available ? '' : ' is-off'}`}>
              <input
                type="checkbox"
                checked={locked || picked.has(key)}
                disabled={locked || !available}
                onChange={(e) => toggle(key, e.target.checked)}
              />
              <span className="rmc-channel__icon" aria-hidden="true"><Icon /></span>
              <span className="rmc-channel__text">
                <span className="rmc-channel__name">{t(`reminders.channel.${key}`)}</span>
                <span className="rmc-channel__hint">{hint}</span>
              </span>
            </label>
          );
        })}
      </div>

      <label className={`ui-switch-row rmc-fallback${smsOn ? '' : ' is-off'}`} htmlFor={`${uid}-fallback`}>
        <span className="rmc-fallback__text">
          <span className="rmc-fallback__name">{t('reminders.channels.smsFallback')}</span>
          <span className="rmc-channel__hint">{t('reminders.channels.smsFallbackHint')}</span>
        </span>
        <input
          id={`${uid}-fallback`}
          type="checkbox"
          role="switch"
          className="ui-switch"
          checked={Boolean(smsFallbackOnly)}
          disabled={!smsOn}
          onChange={(e) => onSmsFallbackChange(e.target.checked)}
        />
      </label>
    </fieldset>
  );
}

